/**
 * CardRenderer — renders patch / run / lookup cards inside an assistant
 * message. Rich card rendering with collapsible sections, field-level diffs,
 * and specialized lookup card views (persona, PDO, brand voice).
 */

import { useState } from 'react';
import type { ResultCard, PatchCard, RunCard, LookupCard } from '../hooks/useBriefChat';
import {
  RecommendationRow,
  type ProductRecommendationRow,
} from './RecommendationRow';

export function CardRenderer({
  cards,
  onApplyProducts,
}: {
  cards: ResultCard[];
  onApplyProducts?: (recommendations: ProductRecommendationRow[]) => void;
}) {
  if (!cards.length) return null;
  return (
    <div className="mt-2 space-y-2">
      {cards.map((c) => {
        if (c.kind === 'patch') return <PatchCardView key={c.patch_id} card={c} />;
        if (c.kind === 'run') return <RunCardView key={c.run_id} card={c} />;
        return (
          <LookupCardView
            key={c.lookup_id}
            card={c}
            onApplyProducts={onApplyProducts}
          />
        );
      })}
    </div>
  );
}

function StatusDot({ status }: { status: string }) {
  const color =
    status === 'completed'
      ? 'bg-emerald-400'
      : status === 'failed'
        ? 'bg-rose-400'
        : status === 'processing'
          ? 'bg-amber-400 animate-pulse'
          : 'bg-slate-300';
  return <span className={`inline-block w-1.5 h-1.5 rounded-full ${color}`} />;
}

function PatchCardView({ card }: { card: PatchCard }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border border-slate-200 rounded-lg bg-slate-50">
      <div
        className="flex items-center gap-2 px-2.5 py-1.5 text-[11px] cursor-pointer"
        onClick={() => setExpanded((v) => !v)}
      >
        <StatusDot status={card.status} />
        <span className="font-semibold text-slate-700 capitalize">
          {card.section.replace(/_/g, ' ')}
        </span>
        <span className="text-slate-400 flex-1 truncate">{card.summary}</span>
        {card.status === 'completed' && (
          <span className="text-[9px] text-slate-400">{expanded ? '▲' : '▼'}</span>
        )}
      </div>
      {card.status === 'failed' && card.error && (
        <p className="text-[10px] text-rose-600 px-2.5 pb-1.5">{card.error}</p>
      )}
      {card.status === 'completed' && expanded && (
        <div className="px-2.5 pb-2">
          <FieldDiff before={card.before} after={card.after} />
        </div>
      )}
    </div>
  );
}

function FieldDiff({
  before,
  after,
}: {
  before: Record<string, unknown> | null | undefined;
  after: Record<string, unknown> | null | undefined;
}) {
  if (!before && !after) return null;

  const allKeys = Array.from(
    new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})]),
  );

  const changedKeys = allKeys.filter((k) => {
    const bv = JSON.stringify((before ?? {})[k]);
    const av = JSON.stringify((after ?? {})[k]);
    return bv !== av;
  });

  if (changedKeys.length === 0) {
    return <p className="text-[10px] text-slate-400 italic mt-1">No field changes detected.</p>;
  }

  return (
    <div className="mt-2 space-y-1.5">
      {changedKeys.map((key) => {
        const bv = (before ?? {})[key];
        const av = (after ?? {})[key];
        return (
          <div key={key} className="rounded border border-slate-100 overflow-hidden">
            <div className="px-2 py-0.5 bg-slate-100 text-[9px] font-mono uppercase tracking-wide text-slate-500">
              {key.replace(/_/g, ' ')}
            </div>
            {bv !== undefined && (
              <div className="px-2 py-1 bg-rose-50 text-[11px] text-rose-700 line-through break-words">
                {formatFieldValue(bv)}
              </div>
            )}
            {av !== undefined && (
              <div className="px-2 py-1 bg-emerald-50 text-[11px] text-emerald-700 break-words">
                {formatFieldValue(av)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function formatFieldValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (Array.isArray(v)) return v.map(String).join(', ') || '(empty list)';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

function RunCardView({ card }: { card: RunCard }) {
  if (card.skill === 'brief_draft' && card.payload.sections) {
    return <BriefDraftRunCard card={card} />;
  }

  const [expanded, setExpanded] = useState(false);
  const angles = (card.payload.angles as Array<Record<string, unknown>>) || [];
  const hasAngles = card.skill === 'angles' && angles.length > 0;

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
      <div
        className={`flex items-center gap-2 text-[11px] px-2.5 py-1.5 ${hasAngles ? 'cursor-pointer' : ''}`}
        onClick={() => hasAngles && setExpanded((v) => !v)}
      >
        <StatusDot status={card.status} />
        <span className="font-semibold text-slate-700 capitalize">
          {card.skill.replace(/_/g, ' ')}
        </span>
        <span className="text-slate-400 flex-1 truncate">{card.summary}</span>
        {hasAngles && (
          <span className="text-[9px] text-slate-400">{expanded ? '▲' : '▼'}</span>
        )}
      </div>

      {card.handoff_share_url && (
        <div className="px-2.5 py-1.5 border-t border-slate-100">
          <a
            href={card.handoff_share_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[11px] font-medium text-brand-teal hover:text-brand-teal-dark"
          >
            Open shareable pack &rarr;
          </a>
        </div>
      )}

      {card.status === 'failed' && card.error && (
        <p className="text-[10px] text-rose-600 px-2.5 py-1">{card.error}</p>
      )}

      {hasAngles && expanded && (
        <div className="border-t border-slate-100 divide-y divide-slate-100">
          {angles.slice(0, 5).map((a, i) => (
            <div key={i} className="px-2.5 py-2">
              <p className="text-[11px] font-semibold text-slate-700 mb-0.5">
                {i + 1}. {(a.hook as string) || (a.title as string) || `Angle ${i + 1}`}
              </p>
              {Boolean(a.message || a.description) && (
                <p className="text-[11px] text-slate-600">
                  {String(a.message || a.description)}
                </p>
              )}
              {Boolean(a.emotional_lever) && (
                <p className="text-[10px] text-violet-600 mt-0.5">
                  Lever: {String(a.emotional_lever)}
                </p>
              )}
              {Boolean(a.rationale) && (
                <p className="text-[10px] text-slate-500 mt-0.5 italic">
                  {String(a.rationale)}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function LookupCardView({
  card,
  onApplyProducts,
}: {
  card: LookupCard;
  onApplyProducts?: (recommendations: ProductRecommendationRow[]) => void;
}) {
  if (card.kind_of === 'persona') return <PersonaLookupCard card={card} />;
  if (card.kind_of === 'pdo') return <PdoLookupCard card={card} />;
  if (card.kind_of === 'brand_voice') return <BrandVoiceLookupCard card={card} />;
  if (card.kind_of === 'products') {
    return <ProductsLookupCard card={card} onApplyProducts={onApplyProducts} />;
  }
  return <DefaultLookupCard card={card} />;
}

function ProductsLookupCard({
  card,
  onApplyProducts,
}: {
  card: LookupCard;
  onApplyProducts?: (recommendations: ProductRecommendationRow[]) => void;
}) {
  const [dismissed, setDismissed] = useState(false);
  const [applying, setApplying] = useState(false);

  if (dismissed) return null;

  const recommendations = (card.payload.recommendations as ProductRecommendationRow[]) ?? [];

  const handleApply = async () => {
    if (!onApplyProducts || recommendations.length === 0) return;
    setApplying(true);
    try {
      await onApplyProducts(recommendations);
      setDismissed(true);
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="border border-teal-100 rounded-lg overflow-hidden bg-white">
      <div className="flex items-center gap-2 px-2.5 py-1.5 bg-teal-50">
        <span className="text-[9px] px-1.5 py-0.5 rounded bg-teal-100 text-teal-700 font-mono uppercase tracking-wider">
          products
        </span>
        <span className="text-[11px] text-teal-800 flex-1 truncate">{card.summary}</span>
      </div>
      <div className="px-2.5 py-2 space-y-1.5">
        {recommendations.map((r, i) => (
          <RecommendationRow key={r.product_id} recommendation={r} rank={i + 1} compact />
        ))}
        {recommendations.length === 0 && (
          <p className="text-[10px] text-slate-400 italic">No product recommendations in payload.</p>
        )}
      </div>
      <div className="px-2.5 py-2 border-t border-teal-100 flex items-center gap-2 bg-slate-50/50">
        <button
          type="button"
          onClick={() => void handleApply()}
          disabled={applying || recommendations.length === 0 || !onApplyProducts}
          className="px-3 py-1.5 text-[11px] font-semibold text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:opacity-40 transition"
        >
          {applying ? 'Applying…' : 'Apply to brief'}
        </button>
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="px-2 py-1.5 text-[11px] text-slate-500 hover:text-slate-700 transition"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

function PersonaLookupCard({ card }: { card: LookupCard }) {
  const answer = (card.payload.answer as string) || card.summary;
  const personaId = card.payload.persona_id as string | undefined;
  const [expanded, setExpanded] = useState(false);

  const extraKeys = Object.keys(card.payload).filter(
    (k) => !['answer', 'persona_id', 'question', 'warning'].includes(k),
  );

  return (
    <div className="border border-violet-100 rounded-lg overflow-hidden">
      <div
        className="flex items-center gap-2 px-2.5 py-1.5 bg-violet-50 cursor-pointer"
        onClick={() => setExpanded((v) => !v)}
      >
        <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 font-mono uppercase tracking-wider">
          persona
        </span>
        {personaId && (
          <span className="text-[10px] text-violet-600 font-medium truncate">
            {personaId.replace(/_/g, ' ')}
          </span>
        )}
        <span className="ml-auto text-[9px] text-violet-400">{expanded ? '▲' : '▼'}</span>
      </div>
      <div className="px-2.5 py-2 bg-white">
        <p className="text-[12px] text-slate-700 leading-snug">{answer}</p>
        {expanded && extraKeys.length > 0 && (
          <div className="mt-2 pt-2 border-t border-slate-100 space-y-1">
            {Object.entries(card.payload)
              .filter(([k]) => extraKeys.includes(k))
              .slice(0, 6)
              .map(([k, v]) => (
                <div key={k} className="flex gap-2 text-[10px]">
                  <span className="text-slate-400 font-mono uppercase tracking-wide shrink-0 w-28 truncate">
                    {k.replace(/_/g, ' ')}
                  </span>
                  <span className="text-slate-600 break-words">{formatFieldValue(v)}</span>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PdoLookupCard({ card }: { card: LookupCard }) {
  const [expanded, setExpanded] = useState(false);
  const hasData = Boolean(card.payload.concept_context || card.payload.scenario_context);

  return (
    <div className="border border-amber-100 rounded-lg overflow-hidden">
      <div
        className="flex items-center gap-2 px-2.5 py-1.5 bg-amber-50 cursor-pointer"
        onClick={() => setExpanded((v) => !v)}
      >
        <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-mono uppercase tracking-wider">
          pdo
        </span>
        <span className="text-[10px] text-amber-700 flex-1 truncate">{card.summary}</span>
        {hasData && (
          <span className="ml-auto text-[9px] text-amber-400">{expanded ? '▲' : '▼'}</span>
        )}
      </div>
      {Boolean(card.payload.warning) && (
        <p className="text-[10px] text-amber-600 px-2.5 py-1 bg-amber-50/50">
          {'⚠'} {String(card.payload.warning)}
        </p>
      )}
      {expanded && hasData && (
        <div className="px-2.5 py-2 bg-white space-y-2">
          <PdoEdgeList payload={card.payload} />
        </div>
      )}
    </div>
  );
}

function PdoEdgeList({ payload }: { payload: Record<string, unknown> }) {
  const ctx = (payload.concept_context || payload.scenario_context) as
    | Record<string, unknown>
    | undefined;
  if (!ctx) return null;

  const edges = (ctx.edges || ctx.causal_edges || []) as Array<Record<string, unknown>>;
  const concepts = (ctx.concepts || []) as Array<Record<string, unknown>>;

  if (edges.length === 0 && concepts.length === 0) {
    return <p className="text-[10px] text-slate-400 italic">No edges available.</p>;
  }

  return (
    <>
      {edges.slice(0, 8).map((e, i) => {
        const rel = (e.relation || e.edge_type || '→') as string;
        const target = (e.target || e.concept || '') as string;
        const weight = e.weight != null ? Number(e.weight).toFixed(2) : null;
        const relColor = rel.toLowerCase().includes('inhibit')
          ? 'text-rose-500'
          : rel.toLowerCase().includes('drives')
            ? 'text-emerald-600'
            : 'text-slate-500';
        return (
          <div key={i} className="flex items-center gap-1.5 text-[11px]">
            <span className={`font-mono text-[9px] uppercase ${relColor} shrink-0 w-20 truncate`}>
              {rel}
            </span>
            <span className="text-slate-700 flex-1 truncate">
              {String(target).replace(/_/g, ' ')}
            </span>
            {weight && <span className="text-[9px] text-slate-400 shrink-0">{weight}</span>}
          </div>
        );
      })}
      {concepts.slice(0, 6).map((c, i) => (
        <div key={i} className="text-[11px] text-slate-600 flex items-center gap-1">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-300 shrink-0" />
          {String(c.name || c.concept_id || c).replace(/_/g, ' ')}
        </div>
      ))}
    </>
  );
}

function BrandVoiceLookupCard({ card }: { card: LookupCard }) {
  const findings = (card.payload.findings as string[]) || [];
  const passed = findings.length === 0 && !card.payload.warning;

  return (
    <div
      className={`border rounded-lg overflow-hidden ${passed ? 'border-emerald-100' : 'border-orange-100'}`}
    >
      <div
        className={`flex items-center gap-2 px-2.5 py-1.5 ${passed ? 'bg-emerald-50' : 'bg-orange-50'}`}
      >
        <span
          className={`text-[9px] px-1.5 py-0.5 rounded font-mono uppercase tracking-wider ${
            passed ? 'bg-emerald-100 text-emerald-700' : 'bg-orange-100 text-orange-700'
          }`}
        >
          brand
        </span>
        <span className={`text-[11px] flex-1 ${passed ? 'text-emerald-700' : 'text-orange-700'}`}>
          {card.summary}
        </span>
        <span className="text-lg">{passed ? '✓' : '⚠'}</span>
      </div>
      {findings.length > 0 && (
        <ul className="px-2.5 py-2 bg-white space-y-1">
          {findings.map((f, i) => (
            <li key={i} className="text-[11px] text-orange-700 flex items-start gap-1.5">
              <span className="shrink-0 mt-0.5">{'•'}</span>
              {f}
            </li>
          ))}
        </ul>
      )}
      {Boolean(card.payload.warning) && (
        <p className="text-[10px] text-slate-500 px-2.5 py-1 italic">
          {String(card.payload.warning)}
        </p>
      )}
    </div>
  );
}

function DefaultLookupCard({ card }: { card: LookupCard }) {
  const [expanded, setExpanded] = useState(false);
  const hasPayload = Object.keys(card.payload).length > 0;

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
      <div
        className={`flex items-center gap-2 px-2.5 py-1.5 text-[11px] ${hasPayload ? 'cursor-pointer' : ''}`}
        onClick={() => hasPayload && setExpanded((v) => !v)}
      >
        <span className="text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">
          {card.kind_of}
        </span>
        <span className="text-slate-600 flex-1 truncate">{card.summary}</span>
        {hasPayload && (
          <span className="text-[9px] text-slate-400">{expanded ? '▲' : '▼'}</span>
        )}
      </div>
      {expanded && hasPayload && (
        <div className="px-2.5 py-2 border-t border-slate-100 space-y-1">
          {Object.entries(card.payload)
            .slice(0, 8)
            .map(([k, v]) => (
              <div key={k} className="flex gap-2 text-[10px]">
                <span className="text-slate-400 font-mono uppercase tracking-wide shrink-0 w-28 truncate">
                  {k.replace(/_/g, ' ')}
                </span>
                <span className="text-slate-600 break-words">{formatFieldValue(v)}</span>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

function BriefDraftRunCard({ card }: { card: RunCard }) {
  const [expanded, setExpanded] = useState(true);
  const [selectedAngle, setSelectedAngle] = useState<number | null>(null);
  const sections = card.payload.sections as Record<string, string> | undefined;
  const angles = (card.payload.angles || []) as Array<Record<string, unknown>>;
  const nextActions = (card.payload.suggested_next_actions || []) as Array<Record<string, unknown>>;

  const PDO_CATEGORY_COLOR: Record<string, string> = {
    NEED: 'bg-rose-50 text-rose-600',
    VALUE: 'bg-emerald-50 text-emerald-600',
    TRUST: 'bg-sky-50 text-sky-600',
    ATTRIBUTE: 'bg-slate-100 text-slate-600',
  };

  return (
    <div className="border border-violet-100 rounded-xl overflow-hidden bg-white shadow-sm">
      <div
        className="flex items-center gap-2 px-3 py-2 bg-violet-50 cursor-pointer"
        onClick={() => setExpanded((v) => !v)}
      >
        <StatusDot status={card.status} />
        <span className="text-[9px] px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 font-mono uppercase tracking-wider">
          brief draft
        </span>
        <span className="text-[11px] text-violet-700 flex-1 truncate">{card.summary}</span>
        <span className="text-[9px] text-violet-400">{expanded ? '▲' : '▼'}</span>
      </div>

      {card.status === 'failed' && card.error && (
        <p className="text-[10px] text-rose-600 px-3 py-1.5">{card.error}</p>
      )}

      {expanded && sections && (
        <>
          <div className="px-3 py-3 space-y-3 border-b border-slate-100">
            {sections.objective && (
              <DraftSection label="Objective" text={sections.objective} color="text-sky-700" />
            )}
            {sections.audience_snapshot && (
              <DraftSection label="Audience" text={sections.audience_snapshot} color="text-violet-700" />
            )}
            {sections.insight && (
              <DraftSection label="Insight" text={sections.insight} color="text-amber-700" />
            )}
          </div>

          {angles.length > 0 && (
            <div className="divide-y divide-slate-100">
              {angles.map((angle, i) => {
                const isSelected = selectedAngle === i;
                const factors = (angle.driving_pdo_factors || []) as Array<Record<string, string>>;
                const causalPath = (angle.causal_path_snippet || []) as Array<Record<string, string>>;
                const intents = (angle.sample_keyword_intents || []) as string[];

                return (
                  <div
                    key={(angle.angle_id as string) || i}
                    className={`px-3 py-2.5 cursor-pointer transition ${isSelected ? 'bg-slate-50' : 'hover:bg-slate-50/50'}`}
                    onClick={() => setSelectedAngle(isSelected ? null : i)}
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-[9px] font-mono text-slate-400 mt-0.5 shrink-0 w-4">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-[12px] font-semibold text-slate-800 leading-snug">
                          {String(angle.hook ?? '')}
                        </p>
                        {Boolean(angle.message) && (
                          <p className="text-[11px] text-slate-600 mt-0.5">{String(angle.message)}</p>
                        )}
                        {Boolean(angle.emotional_lever) && (
                          <span className="text-[9px] text-violet-500 mt-0.5 inline-block">
                            {String(angle.emotional_lever)}
                          </span>
                        )}
                      </div>
                    </div>

                    {isSelected && (
                      <div className="mt-2 ml-6 space-y-1.5">
                        {Boolean(angle.strategic_rationale) && (
                          <p className="text-[10px] text-slate-600">{String(angle.strategic_rationale)}</p>
                        )}
                        {factors.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {factors.slice(0, 4).map((f, j) => (
                              <span
                                key={j}
                                className={`text-[8px] px-1.5 py-0.5 rounded font-mono ${
                                  PDO_CATEGORY_COLOR[f.category] || PDO_CATEGORY_COLOR.ATTRIBUTE
                                }`}
                              >
                                {(f.concept || '').replace(/_/g, ' ')}
                              </span>
                            ))}
                          </div>
                        )}
                        {causalPath.length > 0 && (
                          <div className="text-[9px] text-slate-400 font-mono mt-1">
                            {causalPath.map((e, j) => (
                              <span key={j}>
                                {e.source}{' '}
                                <span className="text-amber-500">
                                  {'→'}[{e.edge_type}]{'→'}
                                </span>{' '}
                                {e.target}
                                {j < causalPath.length - 1 ? ' · ' : ''}
                              </span>
                            ))}
                          </div>
                        )}
                        {intents.length > 0 && (
                          <p className="text-[9px] text-slate-500">
                            Intents: {intents.join(' · ')}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {nextActions.length > 0 && (
            <div className="px-3 py-2.5 bg-slate-50 border-t border-slate-100 flex flex-wrap gap-2">
              {nextActions.map((action, i) => {
                const actionType = action.action as string;
                const colorMap: Record<string, string> = {
                  promote_to_brief: 'bg-brand-purple text-white hover:bg-brand-purple/90',
                  explore_angle: 'bg-violet-50 border border-violet-200 text-violet-700 hover:bg-violet-100',
                  regenerate_angles: 'bg-slate-100 text-slate-600 hover:bg-slate-200',
                };
                return (
                  <button
                    key={i}
                    className={`px-3 py-1 rounded-full text-[11px] font-medium transition cursor-pointer ${
                      colorMap[actionType] || 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    {action.label as string}
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function DraftSection({ label, text, color }: { label: string; text: string; color: string }) {
  return (
    <div>
      <p className={`text-[9px] font-mono uppercase tracking-wider ${color} mb-0.5`}>{label}</p>
      <p className="text-[12px] text-slate-700 leading-snug">{text}</p>
    </div>
  );
}
